import { useState, useEffect, useRef } from 'react';

// mui
import { Box, IconButton } from '@mui/material';

// api
import { Comment, CommentDelete, CommentLike, CommentList, CommentRefresh } from '../../api';

// icons
import { Cancel as CancelIcon } from '@mui/icons-material';

// components
import { CommentListItem } from '..';

// client-shared
import { ContainerScroll, Message, CommonYesNoDialog, MarkdownEditor, DotLoading } from '@goldblack/client-comps';

// shared
import { def } from '@goldblack/shared';

// infinite scroll
import InfiniteScroll from 'react-infinite-scroll-component';

function PostComment(props) {
    const [data, setData] = useState({ loading: false, start: 0, loadmore: false, data: [] });
    const [openDialog, setOpenDialog] = useState(false);
    const [readyToOpen, setReadyToOpen] = useState(false);
    const [replyCommentId, setReplyCommentId] = useState(null);
    const [replyTargetUser, setReplyTargetUser] = useState(null);
    const [deleteCommentId, setDeleteCommentId] = useState(null);
    const [text, setText] = useState('');
    const messagesStartRef = useRef(null);

    const onClickReply = (comment) => {
        if (comment.parentCommentId) {
            setReplyCommentId(comment.parentCommentId);
        } else {
            setReplyCommentId(comment._id);
        }
        setReplyTargetUser(comment.user.username);
        setText('');
    };

    const onClickDelete = (comment) => {
        setDeleteCommentId(comment._id);
        setOpenDialog(true);
    };

    // Update the liked comment in place (no reorder — re-ranking happens on next
    // open/refetch). buildCommentTree preserves root order, so positions stay put.
    const onToggleLike = (commentId) => {
        const comment = data.data.find((c) => c._id === commentId);
        if (!comment) return;
        CommentLike(comment, (liked, likeCount) => {
            setData((prev) => {
                const data = prev.data.map((c) => (c._id === commentId ? { ...c, liked, likeCount } : c));
                return { ...prev, data, treeData: buildCommentTree(data) };
            });
        });
    };

    const scrollToBottom = () => {
        window.requestAnimationFrame(() => messagesStartRef.current?.scrollIntoView({ behavior: 'smooth' }));
    };

    const onClickSend = () => {
        if (text.trim() !== '') {
            sendComment(text);
            setText('');
        }
    };

    const sendComment = (comment) => {
        Comment(props.post._id, comment, replyCommentId, setData, buildCommentTree, () => {
            setReplyCommentId(null);
            setReplyTargetUser(null);
            props.onCommentsChanged?.((props.post.comments ?? 0) + 1);
        });
    };

    const renderCommentList = (comments) => {
        const avatarSize = props.config.commentAvatarSize;
        const halfAvatar = avatarSize / 2 + 4; /*item padding*/
        const replySx = {
            position: 'relative',
            pl: 4,
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '12px',
                width: '1px',
                height: `${halfAvatar}px`,
                borderLeft: '1px solid',
                borderColor: 'divider',
            },
            '&::after': {
                content: '""',
                position: 'absolute',
                top: `${halfAvatar}px`,
                left: '12px',
                width: '12px',
                height: '1px',
                borderTop: '1px solid',
                borderColor: 'divider',
            },
        };

        return comments.map((comment) => (
            <Box key={comment._id} sx={comment.parentCommentId ? replySx : undefined}>
                <CommentListItem
                    config={props.config}
                    key={comment._id}
                    {...comment}
                    onClickReply={() => onClickReply(comment)}
                    onClickDelete={() => onClickDelete(comment)}
                    onToggleLike={() => onToggleLike(comment._id)}
                />
                {comment.replies?.length > 0 && renderCommentList(comment.replies)}
            </Box>
        ));
    };

    const buildCommentTree = (comments) => {
        const map = new Map();

        for (const comment of comments) {
            map.set(comment._id, { ...comment, replies: [] });
        }

        const roots = [];

        for (const comment of comments) {
            const node = map.get(comment._id);
            if (comment.parentCommentId) {
                const parent = map.get(comment.parentCommentId);
                if (parent) {
                    parent.replies.push(node);

                    // nested reply is time order (older one is on the top)
                    parent.replies.sort((a, b) => new Date(a.date) - new Date(b.date));
                } else {
                    // 👇 삭제된 부모 댓글을 가짜로 생성
                    const parentCommentId = comment.parentCommentId;
                    if (!map.has(parentCommentId)) {
                        const deletedParent = {
                            _id: parentCommentId,
                            content: 'deleted comment',
                            user: {
                                online: false,
                                username: 'unknown',
                                image: null,
                                thumbnail: null,
                            },
                            deleted: true,
                            replies: [],
                            date: null,
                        };
                        map.set(parentCommentId, deletedParent);
                        roots.push(deletedParent);
                    }

                    map.get(parentCommentId).replies.push(node);

                    // 대댓글 정렬
                    map.get(parentCommentId).replies.sort((a, b) => new Date(a.date) - new Date(b.date));
                }
            } else {
                roots.push(node);
            }
        }

        return roots;
    };

    const loadMoreRows = (onSucces) => {
        CommentList(
            props.post._id,
            data.start,
            data.start + props.config.userListLimit,
            setData,
            buildCommentTree,
            onSucces,
        );
    };

    const onYes = () => {
        setOpenDialog(false);

        // delete comment
        const hasReplies = data.data.some((c) => c.parentCommentId === deleteCommentId);
        CommentDelete(props.post._id, deleteCommentId, hasReplies, setData, buildCommentTree, () => {
            // A comment with replies stays as a "deleted" placeholder and still counts; one without leaves the count.
            if (!hasReplies) props.onCommentsChanged?.(Math.max(0, (props.post.comments ?? 0) - 1));
        });
    };

    const onNo = () => {
        setOpenDialog(false);
    };

    const onClose = () => {
        setOpenDialog(false);
    };

    // A comment is PROCESSING until the text check ends (seconds, longer under load). Re-read the loaded part of the list
    // and flip the ones that got a verdict in place; the gaps grow like the post status poll and stop when none is left.
    const processingKey = data.data
        .filter((c) => c.processing)
        .map((c) => c._id)
        .join(',');
    useEffect(() => {
        if (!props.open || !readyToOpen || !processingKey) return;
        let cancelled = false;
        let timer = null;
        const startedAt = Date.now();
        const tick = async () => {
            const fresh = await CommentRefresh(processingKey.split(','));
            if (cancelled) return;
            if (processingKey.split(',').some((id) => fresh[id] && fresh[id].status !== def.PostStatus.PROCESSING)) {
                setData((prev) => {
                    const list = prev.data.map((c) => {
                        const f = c.processing && fresh[c._id];
                        return f && f.status !== def.PostStatus.PROCESSING
                            ? {
                                  ...c,
                                  processing: false,
                                  content: f.content,
                                  removed: f.status === def.PostStatus.CENSORED,
                              }
                            : c;
                    });
                    return { ...prev, data: list, treeData: buildCommentTree(list) };
                });
                return; // processingKey changes, the effect restarts for whatever is still processing
            }
            const elapsed = Date.now() - startedAt;
            const delay = elapsed < 20000 ? 1500 : elapsed < 60000 ? 5000 : elapsed < 300000 ? 15000 : null;
            if (delay) timer = setTimeout(tick, delay);
        };
        timer = setTimeout(tick, 1500);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [processingKey, props.open, readyToOpen]);

    useEffect(() => {
        if (props.open) {
            loadMoreRows(() => {
                setReadyToOpen(true);
            });
        } else {
            setReadyToOpen(false);
        }
        return () => {
            setReadyToOpen(false);
        };
    }, [props.open]);

    return (
        readyToOpen && (
            <>
                <ContainerScroll id='chatScroll' sx={{ p: 1, width: '100%' }}>
                    {data.loading && data.data.length === 0 ? (
                        <DotLoading variant='pulse' sx={{ height: '100%' }} />
                    ) : data.data.length > 0 ? (
                        <InfiniteScroll
                            dataLength={data.data.length}
                            next={loadMoreRows}
                            hasMore={data.loadmore}
                            scrollThreshold='200px'
                            scrollableTarget='chatScroll'>
                            <div ref={messagesStartRef} />
                            {renderCommentList(data.treeData)}
                        </InfiniteScroll>
                    ) : (
                        <Message variant='subtitle2' message='No Comments' />
                    )}
                </ContainerScroll>
                {replyTargetUser && (
                    <Box
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                        <Box component='span' sx={{ fontSize: 12, color: 'text.secondary' }}>
                            Replying to @{replyTargetUser}
                        </Box>
                        <IconButton
                            size='small'
                            onClick={() => {
                                setReplyCommentId(null);
                                setReplyTargetUser(null);
                            }}>
                            <CancelIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Box>
                )}
                <MarkdownEditor
                    text={text}
                    setText={setText}
                    placeholder='Add a comment...'
                    onSend={onClickSend}
                    maxLength={props.config.commentDescMax}
                />
                {/*===== delete confirm dialog =====*/}
                <CommonYesNoDialog
                    open={openDialog}
                    title={''}
                    desc='Delete comment?'
                    onClose={onClose}
                    onYes={onYes}
                    onNo={onNo}
                />
            </>
        )
    );
}

export { PostComment };
